// Who is using the app, and in what capacity.
//
// This is NOT a Solid identity. The institution has exactly one institutional
// WebID — the identity partners grant access to, held by the backend. Its staff
// are told apart by *role* (roles.md: technical / governance / analyst), which
// is an org-internal concern. In a real deployment this comes from the
// institution's own SSO; here the picker stands in for it, and the backend
// enforces what each role may do.
//
// See tech-spec.md § "Decision — authentication (2026-07-16)".

import { ROLES, SELF } from '../../shared/config.js';

const STORAGE_KEY = 'solidshare.role';

export const role = $state({
  current: null, // role id, or null when nobody has picked one
  context: null, // { self, institution, partners, roles, publishRole } from the backend
  institution: null, // { name, label, tagline, accent } — Pod-resident brand
  health: null, // { ok, actingAs } — proves the held credential works
  error: null,
});

// Presentation identity comes from the Pod (institution.json via /api/context).
// When it's absent — a Pod without the file, or the backend unreachable — fall
// back to the slug so the app renders rather than breaking.
const fallbackInstitution = () => ({
  name: SELF.institution, label: SELF.institution, tagline: '', accent: null,
});

export const institution = () => role.institution ?? fallbackInstitution();

// Apply the Pod-resident identity at boot: the brand + tagline + document.title,
// and the accent colour as a root CSS custom property (overriding the theme
// default). Editing institution.json in the Pod changes all of these on reload,
// with no rebuild.
export function applyContext(ctx) {
  role.context = ctx;
  if (ctx?.self) Object.assign(SELF, ctx.self);
  const inst = ctx?.institution ?? fallbackInstitution();
  role.institution = inst;
  if (inst.name) document.title = inst.tagline ? `${inst.name} — ${inst.tagline}` : inst.name;
  if (inst.accent) document.documentElement.style.setProperty('--accent', inst.accent);
}

export const currentRole = () => (role.current ? ROLES[role.current] : null);

export function restore() {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved && ROLES[saved]) role.current = saved;
}

export function signIn(id) {
  if (!ROLES[id]) return;
  role.current = id;
  localStorage.setItem(STORAGE_KEY, id);
}

export function signOut() {
  role.current = null;
  localStorage.removeItem(STORAGE_KEY);
}

// May the current role reach this route? Roles are about scope of work, not
// secrecy — the Pod's own WAC is the real boundary — so this shapes navigation
// rather than pretending to be a security control. The one act with real
// consequences (publishing, which writes WAC) is enforced in the backend too.
export function mayVisit(route) {
  route = String(route).split('?')[0];
  if (!role.current) return false;
  if (route === 'dashboard' || route === 'login') return true;
  return ROLES[role.current].screens.includes(route);
}
